// Apolo IDE — Spec Gate engine (S3 Epic E3a).
//
// PURE-ISH renderer lib. chatFn is INJECTED (multi-LLM LAW — never hardcode
// a model/provider). Spec generation is DEFENSIVE: parse failures return null;
// nothing here must ever throw or break a turn.

const DESIGN_MAX_CHARS = 4000;
const LIST_MAX_ITEMS = 12;
const ITEM_MAX_CHARS = 500;

// ── helpers ─────────────────────────────────────────────────────────────────

/** Strip C0/C1 control chars except \n and \t (which are legit in markdown). */
function stripControl(str) {
  // eslint-disable-next-line no-control-regex
  return str.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
}

/** Extract the first balanced {...} from a string. Returns null on failure.
 *
 * Strategy:
 * 1. Try JSON.parse of the substring from the first '{' — covers clean JSON-only responses.
 * 2. If that fails, walk character-by-character tracking string context (respecting \" escapes)
 *    and only count '{'/'}' when NOT inside a double-quoted string, returning the first
 *    balanced object. This handles JSON where string values contain '{' or '}'.
 */
function extractFirstObject(text) {
  const start = text.indexOf('{');
  if (start === -1) return null;

  // Fast path: try parsing from the first '{' to end-of-text directly.
  // JSON.parse itself is the most robust brace-balanced extractor for clean responses.
  const tail = text.slice(start);
  try {
    JSON.parse(tail);
    return tail;
  } catch {
    // Fall through to the character-walking approach
  }

  // Slow path: walk the substring tracking string literals so we only count
  // unquoted '{' / '}' characters, and stop at the first balanced close.
  let depth = 0;
  let inString = false;
  let escape = false;
  for (let i = 0; i < tail.length; i++) {
    const ch = tail[i];
    if (escape) {
      escape = false;
      continue;
    }
    if (ch === '\\' && inString) {
      escape = true;
      continue;
    }
    if (ch === '"') {
      inString = !inString;
      continue;
    }
    if (!inString) {
      if (ch === '{') depth++;
      else if (ch === '}') {
        depth--;
        if (depth === 0) return tail.slice(0, i + 1);
      }
    }
  }
  return null;
}

function safeUUID() {
  try {
    return crypto.randomUUID();
  } catch {
    // fallback for envs that don't have crypto (tests, etc.)
    return `spec-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

// ── sanitizeSpec ─────────────────────────────────────────────────────────────

/**
 * Sanitize a raw spec-shaped object into a valid InlineSpec or null.
 * Never throws.
 *
 * @param {*} raw
 * @returns {import('./specGate').InlineSpec|null}
 */
export function sanitizeSpec(raw) {
  try {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;

    // design
    let design = typeof raw.design === 'string' ? raw.design : '';
    design = stripControl(design);
    if (design.length > DESIGN_MAX_CHARS) design = design.slice(0, DESIGN_MAX_CHARS);

    // list sanitizer
    const sanitizeList = (src) => {
      if (!Array.isArray(src)) return [];
      return src
        .slice(0, LIST_MAX_ITEMS)
        .map((item) => (typeof item === 'string' ? stripControl(item).trim() : ''))
        .filter((item) => item.length > 0)
        .map((item) => (item.length > ITEM_MAX_CHARS ? item.slice(0, ITEM_MAX_CHARS) : item));
    };

    const requirements = sanitizeList(raw.requirements);
    const acceptance = sanitizeList(raw.acceptance);

    // Skip-safe: if nothing survived, return null
    if (!design && requirements.length === 0 && acceptance.length === 0) return null;

    return {
      id: typeof raw.id === 'string' && raw.id ? raw.id : safeUUID(),
      design,
      requirements,
      acceptance,
      ephemeral: true,
    };
  } catch {
    return null;
  }
}

// ── detectContradictions (E1 — symbolic spec-check, no LLM in the verdict) ────
//
// Kiro-style automated-reasoning pattern adapted honestly to the Apolo
// no-LLM-judge doctrine: the LLM only produced the EARS requirement strings
// earlier (generateSpec). THIS function's verdict — is there a contradiction or
// a gap — is PURE DETERMINISTIC LOGIC over those strings. Same input → same
// output, no model call. Never throws; malformed input → safe empty verdict.

// Antonym pairs that signal opposite behavior for the same condition.
const ANTONYM_PAIRS = [
  ['enable', 'disable'],
  ['allow', 'block'],
  ['allow', 'deny'],
  ['show', 'hide'],
  ['grant', 'revoke'],
  ['accept', 'reject'],
  ['include', 'exclude'],
  ['add', 'remove'],
  ['open', 'close'],
  ['lock', 'unlock'],
];

/** Parse an EARS requirement "WHEN <cond> THE SYSTEM SHALL <behavior>" into
 *  { condition, behavior, negated }. Falls back to treating the whole string as
 *  the behavior when it doesn't match EARS. Pure; never throws. */
function parseEars(reqRaw) {
  const req = typeof reqRaw === 'string' ? reqRaw.toLowerCase().trim() : '';
  if (!req) return null;
  const shallIdx = req.indexOf(' shall ');
  let condition = '';
  let behavior = req;
  if (shallIdx !== -1) {
    const head = req.slice(0, shallIdx); // "when <cond> the system"
    behavior = req.slice(shallIdx + ' shall '.length).trim();
    condition = head
      .replace(/^when\s+/, '')
      .replace(/\s+the system$/, '')
      .trim();
  }
  // Detect explicit negation right after SHALL ("shall not" already stripped the
  // "shall "; a leading "not " on the behavior is the negation marker).
  let negated = false;
  if (behavior.startsWith('not ')) {
    negated = true;
    behavior = behavior.slice(4).trim();
  }
  return { condition, behavior, negated };
}

/** Normalize a phrase for comparison: collapse whitespace, drop trivial words. */
function normPhrase(s) {
  return String(s || '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** True when two parsed requirements assert opposite behavior for the SAME
 *  condition — either explicit negation (X vs NOT X) or an antonym pair. */
function contradicts(a, b) {
  if (!a || !b) return false;
  // Must share a non-empty condition to be a real contradiction (different WHEN
  // clauses are not contradictory — they are just different rules).
  const condA = normPhrase(a.condition);
  const condB = normPhrase(b.condition);
  if (!condA || condA !== condB) return false;

  const behA = normPhrase(a.behavior);
  const behB = normPhrase(b.behavior);

  // Explicit negation: same behavior text, opposite negated flags.
  if (behA === behB && a.negated !== b.negated) return true;

  // Antonym pair: strip the leading verb and compare the object; if the verbs
  // are an antonym pair and the object matches, it's a contradiction.
  const [verbA, ...restA] = behA.split(' ');
  const [verbB, ...restB] = behB.split(' ');
  const objA = restA.join(' ');
  const objB = restB.join(' ');
  if (objA && objA === objB) {
    for (const [x, y] of ANTONYM_PAIRS) {
      if ((verbA === x && verbB === y) || (verbA === y && verbB === x)) return true;
    }
  }
  return false;
}

/**
 * Symbolic contradiction/gap detector over an InlineSpec (or spec-shaped object).
 * DETERMINISTIC — no LLM. Never throws.
 *
 * @param {{requirements?: string[], acceptance?: string[]}|null} spec
 * @returns {{ contradictions: Array<{i:number,j:number,condition:string}>, gaps: string[], ok: boolean }}
 */
export function detectContradictions(spec) {
  const empty = { contradictions: [], gaps: [], ok: true };
  try {
    if (!spec || typeof spec !== 'object') return empty;
    const requirements = Array.isArray(spec.requirements) ? spec.requirements : [];
    const acceptance = Array.isArray(spec.acceptance) ? spec.acceptance : [];

    const parsed = requirements.map(parseEars);

    // Contradictions: pairwise over parsed requirements sharing a condition.
    const contradictions = [];
    for (let i = 0; i < parsed.length; i++) {
      for (let j = i + 1; j < parsed.length; j++) {
        if (contradicts(parsed[i], parsed[j])) {
          contradictions.push({ i, j, condition: normPhrase(parsed[i].condition) });
        }
      }
    }

    // Gaps: requirements present but no acceptance criteria to verify them.
    const gaps = [];
    const validReqs = requirements.filter((r) => typeof r === 'string' && r.trim());
    const validAcc = acceptance.filter((a) => typeof a === 'string' && a.trim());
    if (validReqs.length > 0 && validAcc.length === 0) {
      gaps.push('requirements present but no acceptance criteria to verify them');
    }

    return {
      contradictions,
      gaps,
      ok: contradictions.length === 0 && gaps.length === 0,
    };
  } catch {
    return empty;
  }
}

// ── generateSpec ─────────────────────────────────────────────────────────────

/**
 * Generate an InlineSpec by prompting the injected chatFn.
 *
 * @param {{ request: string, chatFn: Function, model: string, steering?: Array }} opts
 * @returns {Promise<import('./specGate').InlineSpec|null>}
 */
export async function generateSpec({ request, chatFn, model, steering = [] }) {
  try {
    const steeringContext =
      Array.isArray(steering) && steering.length > 0
        ? `\n\nProject steering context (for awareness only):\n${steering
            .map((s) => `[${s.kind}] ${s.content}`)
            .join('\n')}`
        : '';

    const systemInstruction = `You are a precise technical spec writer. When given a request, output ONLY a single JSON object — no markdown fences, no prose, no explanation. The JSON must be:\n{"design":"<concise markdown design overview>","requirements":["WHEN <condition> THE SYSTEM SHALL <behavior>",...],"acceptance":["<verifiable criterion>",...]}\n\nRules:\n- requirements must follow EARS format: start with WHEN\n- acceptance must be verifiable and specific\n- output ONLY the JSON object, nothing else${steeringContext}`;

    const messages = [
      { role: 'system', content: systemInstruction },
      { role: 'user', content: `Generate a technical spec for the following request:\n\n${request}` },
    ];

    const response = await chatFn({ messages, model });

    if (!response || !response.ok) return null;

    const content = typeof response.content === 'string' ? response.content : '';
    const raw = extractFirstObject(content);
    if (!raw) return null;

    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }

    if (!parsed || typeof parsed !== 'object') return null;

    // Assign a fresh id — never reuse model-generated ids
    parsed.id = safeUUID();
    return sanitizeSpec(parsed);
  } catch {
    return null;
  }
}
